import { AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MultiTechOptimizationProgress } from '../../shared';

interface DayPlanningErrorAlertsProps {
  /** Whether optimization mutation has an error */
  hasOptimizationError: boolean;
  /** The optimization error (if any) */
  optimizationError: Error | null;
  /** Whether optimization is currently pending */
  isOptimizing: boolean;
  /** Callback to retry optimization */
  onRetryOptimize: () => void;
  /** Whether rate limit error exists */
  hasRateLimit: boolean;
  /** Number of failed routes (missing address data) */
  failedRoutesCount: number;
  /** Multi-tech optimization progress (optional, only in multi-tech view) */
  multiTechProgress?: MultiTechOptimizationProgress;
}

/**
 * Day Planning Error Alerts Component
 *
 * Displays error and warning alerts for:
 * - Optimization errors with retry button
 * - Rate limit errors with reload button
 * - Failed routes due to missing address data
 */
export function DayPlanningErrorAlerts({
  hasOptimizationError,
  optimizationError,
  isOptimizing,
  onRetryOptimize,
  hasRateLimit,
  failedRoutesCount,
  multiTechProgress,
}: DayPlanningErrorAlertsProps) {
  return (
    <>
      {/* Multi-tech optimization progress alert */}
      {multiTechProgress?.isOptimizing && multiTechProgress.total > 0 && (
        <div
          className="mb-4 flex items-center gap-2 rounded-md border border-blue-500/50 bg-blue-50
            px-3 py-2 text-sm text-blue-800 dark:bg-blue-950/30 dark:text-blue-200"
        >
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          <span>
            Optimizing routes... ({multiTechProgress.completed}/
            {multiTechProgress.total})
          </span>
        </div>
      )}

      {/* Optimization error alert */}
      {hasOptimizationError && (
        <div
          className="mb-4 flex items-center justify-between gap-3 rounded-md border border-red-500/50
            bg-red-50 px-4 py-3 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>
              {optimizationError instanceof Error
                ? optimizationError.message
                : 'Route optimization failed. Please try again.'}
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onRetryOptimize}
            disabled={isOptimizing}
            className="shrink-0"
          >
            <RefreshCw
              className={cn('mr-1 h-4 w-4', isOptimizing && 'animate-spin')}
            />
            Retry
          </Button>
        </div>
      )}

      {/* Rate limit error alert */}
      {hasRateLimit && (
        <div
          className="mb-4 flex items-center justify-between gap-3 rounded-md border border-red-500/50
            bg-red-50 px-4 py-3 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-200"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>
              Route calculation rate limited. Some routes could not be
              calculated. Please wait a moment and try again.
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.location.reload()}
            className="shrink-0"
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Reload
          </Button>
        </div>
      )}

      {/* Warning for failed routes */}
      {failedRoutesCount > 0 && !hasRateLimit && (
        <div
          className="mb-4 flex items-center gap-2 rounded-md border border-amber-500/50 bg-amber-50
            px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>
            {failedRoutesCount}{' '}
            {failedRoutesCount === 1 ? 'workorder' : 'workorders'} excluded from
            route calculation (incomplete address data)
          </span>
        </div>
      )}
    </>
  );
}
